import { useState } from 'react';
import { toast } from 'sonner';
import { Field } from '../ui/Field';
import { btnGhost, btnSecondary, inputCls } from '../ui/styles';
import { revokeStaffSessions, updateStaffAccess } from '../adminApi';
import type { Guarded } from '../useReauth';
import { ApiError } from '../../core/api/http';
import { dayMedium, today } from '../../core/format/dates';
import { isRoleId, selectableRoles } from '../../core/rules/permissions';
import type { StaffLogin } from '../../core/types/adminRequests';
import type { RoleId } from '../../core/types/team';
type Props = {
  name: string;
  staffId: string;
  login: StaffLogin;
  onLogin: (login: StaffLogin) => void;
  guard: <T>(fn: () => Promise<T>) => Promise<Guarded<T>>;
};
const stamp = (at: string) => `${dayMedium(at.slice(0, 10))}, ${at.slice(11, 16)}`;

/**
 * Team en toegang voor één login: rol, tot wanneer, en waar ze is ingelogd. Gaat meteen naar de
 * server (los van Opslaan) en vraagt een recente bevestiging. Een oudere server zonder rollen: zzp.
 */
export function StaffAccess({
  name,
  staffId,
  login,
  onLogin,
  guard
}: Props) {
  const savedRole: RoleId = login.roles?.find(isRoleId) ?? 'behandelaar-zzp';
  const savedUntil = login.validUntil ?? '';
  const [role, setRole] = useState<RoleId>(savedRole);
  const [until, setUntil] = useState(savedUntil);
  const [busy, setBusy] = useState<'access' | 'sessions' | null>(null);
  const [error, setError] = useState('');
  const roles = selectableRoles();
  const chosen = roles.find(r => r.id === role);
  const dirty = role !== savedRole || until !== savedUntil;
  const untilInvalid = until !== '' && until < today();
  const save = async () => {
    if (busy || !dirty || untilInvalid) return;
    setBusy('access');
    setError('');
    try {
      const res = await guard(() => updateStaffAccess(staffId, {
        roles: [role],
        validUntil: until || null
      }));
      if (res.done) {
        onLogin(res.value);
        toast.success(`Toegang van ${name} aangepast`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Opslaan lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(null);
    }
  };
  const logoutEverywhere = async () => {
    if (busy) return;
    setBusy('sessions');
    try {
      const res = await guard(() => revokeStaffSessions(staffId));
      if (res.done) {
        onLogin(res.value);
        toast(`${name} is overal uitgelogd`, {
          description: 'Haar login blijft; ze logt de volgende keer opnieuw in.'
        });
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Uitloggen lukte niet. Probeer het opnieuw.');
    } finally {
      setBusy(null);
    }
  };
  const sessions = login.sessions ?? [];
  return <div className="mt-3 space-y-3 border-t border-line pt-3" data-id="element-4165" data-mp="1mg9iephmkm6n">
      <div className="grid gap-3 sm:grid-cols-2" data-id="element-4166" data-mp="1mim9pt99w8wd">
        <Field label="Rol" data-id="element-4167" data-mp="3bwr1u74qqqd7">
          {/* Alleen als er echt iets te kiezen is; de rol blijft altijd zichtbaar. */}
          <select value={role} onChange={e => isRoleId(e.target.value) && setRole(e.target.value)} disabled={busy !== null} className={inputCls} data-id="element-4168" data-mp="3guaex3ppcx3k">
            {roles.map(r => <option key={r.id} value={r.id} data-id="element-4169" data-mp="3m0paj8u0s8ub">
                {r.name}
              </option>)}
          </select>
        </Field>
        <Field label="Toegang tot en met" hint="Leeg = geen einddatum. Daarna kan ze niet meer inloggen." data-id="element-4170" data-mp="3nrf9f86a46h">
          <input type="date" value={until} min={today()} onChange={e => setUntil(e.target.value)} disabled={busy !== null} aria-invalid={untilInvalid} className={inputCls} data-id="element-4171" data-mp="3uyhr0gbh9mja" />
        </Field>
      </div>
      {chosen && <p className="text-[12px] text-muted" data-id="element-4172" data-mp="iixoeb2kej41">{chosen.description}</p>}
      {untilInvalid && <p role="alert" className="text-[12px] text-clay" data-id="element-4173" data-mp="2obw1w9a1aj4h">
          Kies vandaag of een dag in de toekomst.
        </p>}
      {error && <p role="alert" className="text-[12px] text-clay" data-id="element-4174" data-mp="2kblvwwm8rspm">
          {error}
        </p>}
      {dirty && <div className="flex flex-wrap gap-2" data-id="element-4175" data-mp="2lqy4awbdv50a">
          <button type="button" onClick={() => void save()} disabled={busy !== null || untilInvalid} aria-busy={busy === 'access'} className={btnSecondary} data-id="element-4176" data-mp="2qqao186yjvyn">
            {busy === 'access' ? 'Opslaan…' : 'Toegang opslaan'}
          </button>
          <button type="button" onClick={() => {
        setRole(savedRole);
        setUntil(savedUntil);
        setError('');
      }} disabled={busy !== null} className={btnGhost} data-id="element-4177" data-mp="2zpxn3nc7spoj">
            Terugzetten
          </button>
        </div>}

      <div data-id="element-4178" data-mp="2bzjyk6su98yp">
        <p className="text-[13px] text-ink" data-id="element-4179" data-mp="2gscymhdd3t4g">Ingelogd op</p>
        {sessions.length === 0 ? <p className="mt-1 text-[13px] text-muted" data-id="element-4180" data-mp="1jcz4gykzairg">Nergens ingelogd.</p> : <ul className="mt-1 space-y-1 text-[13px] text-muted" data-id="element-4181" data-mp="1jawe2dfwynm7">
            {sessions.map(x => <li key={x.id} data-id="element-4182" data-mp="1o9pizhh4x6ar">
                {x.device} · laatst actief {stamp(x.lastActiveAt)}
              </li>)}
          </ul>}
        {sessions.length > 0 && <button type="button" onClick={() => void logoutEverywhere()} disabled={busy !== null} aria-busy={busy === 'sessions'} className={`${btnGhost} mt-2`} data-id="element-4183" data-mp="1brdf2xq9yjrz">
            {busy === 'sessions' ? 'Uitloggen…' : 'Overal uitloggen'}
          </button>}
      </div>
    </div>;
}